import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { listTokens, createToken, revokeToken } from 'services/transparency-config-service';
import { DotsLoader } from 'Components/DotsLoader';
import { formatDateTimeWithTimezone } from 'utils/date_utils';
import { LOCALE_MAP } from 'utils/locale-formatting';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import { Dialog, Select, TextField } from 'Components/ui';
import styled from 'styled-components';
import { Skeleton } from 'Components/common/Skeleton';

const Card = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

const Title = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`

const Subtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 4px 0 0;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;
`

const Divider = styled.div`
  height: 1px;
  background: var(--vo-cloud);
  margin: 16px 0;
`

const TableBorder = styled.div`
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
  overflow: hidden;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const TableHead = styled.thead`
  background: var(--vo-mist);
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:last-child { border-bottom: none; }
  &:hover { background: var(--vo-capri-25); }
`

const TableCell = styled.td`
  padding: 12px 16px;
  vertical-align: middle;
  text-align: ${({ $align }) => $align || 'left'};
`

const TableHeaderCell = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: ${({ $align }) => $align || 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  white-space: nowrap;
`

const CellText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink);
`

const CellMuted = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
`

const CellDate = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
`

const AccessCount = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--vo-celeste);
`

const StatusBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
`

const IconBtn = styled.button`
  width: 30px;
  height: 30px;
  background: none;
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--vo-danger);
  margin: 0 auto;
  &:hover:not(:disabled) { background: var(--vo-danger-10); }
`

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
`

const AlertError = styled(Alert)`
  background-color: var(--vo-danger-10);
  color: var(--vo-danger);
`

const AlertInfo = styled(Alert)`
  background-color: var(--vo-info-8);
  color: var(--vo-celeste);
`

const EmptyAlert = styled(AlertInfo)`margin: 0;`

const CreateTokenBtn = styled.button`
  height: 40px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  &:hover { background: var(--vo-cobalto); }
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`

const Label = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
`

const InfoBox = styled.div`
  background: var(--vo-capri-50);
  border: 1px solid color-mix(in srgb, var(--vo-info) 20%, transparent);
  border-radius: 10px;
  padding: 12px 16px;
`

const InfoBoxText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-celeste);
  margin: 0;
  line-height: 1.55;
`

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
`

const ModalBtn = styled.button`
  height: 44px;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: ${({ $variant }) => $variant === 'secondary' ? '1.5px solid var(--vo-line)' : 'none'};
  background: ${({ $variant }) => $variant === 'secondary' ? 'white' : 'var(--vo-celeste)'};
  color: ${({ $variant }) => $variant === 'secondary' ? 'var(--vo-slate)' : 'white'};
  &:hover { background: ${({ $variant }) => $variant === 'secondary' ? 'var(--vo-mist)' : 'var(--vo-cobalto)'}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const TokenHint = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
  margin: 0 0 6px;
  line-height: 1.55;
`

const TokenWarning = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-danger);
  margin: 0 0 16px;
`

// Bespoke — fixed 4-row height (~100px at 12px/1.6 line-height), monospace,
// readonly display box. multiline already gives TextField a clean sx slate
// (skips CLOSED_SX). rows (not minRows/maxRows) makes MUI's TextareaAutosize
// settle at a fixed height instead of growing with content.
const TOKEN_TEXTAREA_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    background: 'var(--vo-mist)',
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
    fontFamily: 'monospace',
    fontSize: '12px',
    color: 'var(--vo-ink)',
    lineHeight: 1.6,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--vo-steel)',
    borderWidth: '1.5px',
  },
}

const PlusSVG = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
)

const RevokeSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
  </svg>
)

const CopySVG = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
)

const getStatusBadgeProps = (key) => {
  switch (key) {
    case 'active': return { $color: 'var(--vo-success)', $bg: 'var(--vo-success-10)' }
    case 'expired': return { $color: 'var(--vo-danger)', $bg: 'var(--vo-danger-10)' }
    case 'revoked': return { $color: 'var(--vo-slate)', $bg: 'var(--vo-slate-10)' }
    default: return { $color: 'var(--vo-slate)', $bg: 'var(--vo-slate-10)' }
  }
}

export const TokenManagement = ({ config, companyKey }) => {
  const selectedTenant = useSelector(useAuthTenant);
  const companyLocale = LOCALE_MAP[selectedTenant?.defaultLanguage] ?? 'pt-BR';
  const { t } = useTranslation(['transparency', 'common']);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [showTokenResult, setShowTokenResult] = useState(false);
  const [createdToken, setCreatedToken] = useState('');

  const { data: tokensData, isLoading, error, refetch } = useQuery(
    ['transparencyTokens', companyKey],
    () => listTokens(companyKey),
    {
      enabled: !!companyKey,
      staleTime: 1 * 60 * 1000,
    }
  );

  const { control, handleSubmit, reset, setError, clearErrors, formState: { errors } } = useForm({
    defaultValues: {
      recipientEmail: '',
      recipientName: '',
      expirationHours: 24,
    },
  });

  const isEmailAllowed = (email, allowedDomains, allowedEmails) => {
    if (!email || !email.includes('@')) return false;
    const domain = email.split('@')[1];
    return allowedDomains.includes(domain) || allowedEmails.includes(email);
  };

  const createMutation = useMutation(
    (data) => createToken(companyKey, data.recipientEmail, data.recipientName, data.expirationHours),
    {
      onSuccess: (response) => {
        setCreateDialogOpen(false);
        reset();
        refetch();
        setCreatedToken(response.data.token);
        setShowTokenResult(true);
      },
      onError: (error) => {
        Dialog.alert({
          title: t('transparency:tokenManagement.notifications.generalError'),
          message: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const revokeMutation = useMutation(
    (tokenId) => revokeToken(companyKey, tokenId),
    {
      onSuccess: () => {
        Dialog.alert({
          title: t('transparency:tokenManagement.notifications.revokeSuccess'),
          message: t('transparency:tokenManagement.notifications.revokeSuccessText'),
          icon: 'success',
        });
        refetch();
      },
      onError: (error) => {
        Dialog.alert({
          title: t('transparency:tokenManagement.notifications.revokeError'),
          message: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const handleCopyToken = async () => {
    try {
      await navigator.clipboard.writeText(createdToken);
    } catch {
      const textarea = document.getElementById('token-result-string');
      if (textarea) {
        textarea.select();
        document.execCommand('copy');
      }
    }
  };

  const handleCloseTokenResult = () => {
    setShowTokenResult(false);
    setCreatedToken('');
  };

  const onSubmit = (data) => {
    const allowedDomains = config?.allowedEmailDomains || [];
    const allowedEmails = config?.allowedEmails || [];

    if (!isEmailAllowed(data.recipientEmail, allowedDomains, allowedEmails)) {
      setError('recipientEmail', {
        type: 'manual',
        message: t('transparency:tokenManagement.validation.emailNotAllowed'),
      });
      return;
    }

    clearErrors('recipientEmail');
    createMutation.mutate(data);
  };

  const handleRevoke = async (tokenId, recipientEmail) => {
    const confirmed = await Dialog.confirm({
      title: t('transparency:tokenManagement.notifications.revokeTitle'),
      message: t('transparency:tokenManagement.notifications.revokeConfirmText', { email: recipientEmail }),
      confirmLabel: t('transparency:tokenManagement.actions.confirmRevoke'),
      cancelLabel: t('common:buttons.cancel'),
      confirmColor: 'var(--vo-danger)',
    });
    if (confirmed) {
      revokeMutation.mutate(tokenId);
    }
  };

  const getTokenStatus = (token) => {
    if (token.revokedAt) return { label: t('transparency:tokenManagement.status.revoked'), key: 'revoked' };
    if (new Date(token.expiresAt) < new Date()) return { label: t('transparency:tokenManagement.status.expired'), key: 'expired' };
    return { label: t('transparency:tokenManagement.status.active'), key: 'active' };
  };

  const canRevoke = (token) => {
    return !token.revokedAt && new Date(token.expiresAt) >= new Date();
  };

  if (isLoading) {
    return (
      <Card>
        <Skeleton $width={300} $height={40} $marginBottom={8} />
        <Skeleton $width={'100%'} $height={300} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <AlertError>{t('transparency:tokenManagement.error')}</AlertError>
      </Card>
    );
  }

  const tokenList = tokensData?.data || [];

  return (
    <Card>
      <HeaderRow>
        <div>
          <Title>{t('transparency:tokenManagement.title')}</Title>
          <Subtitle>{t('transparency:tokenManagement.subtitle')}</Subtitle>
        </div>
        <CreateTokenBtn onClick={() => setCreateDialogOpen(true)}>
          <PlusSVG />
          {t('transparency:tokenManagement.actions.create')}
        </CreateTokenBtn>
      </HeaderRow>

      <Divider />

      {tokenList.length === 0 ? (
        <EmptyAlert>{t('transparency:tokenManagement.empty')}</EmptyAlert>
      ) : (
        <TableBorder>
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell>{t('transparency:tokenManagement.table.recipientEmail')}</TableHeaderCell>
                <TableHeaderCell>{t('transparency:tokenManagement.table.name')}</TableHeaderCell>
                <TableHeaderCell>{t('transparency:tokenManagement.table.createdAt')}</TableHeaderCell>
                <TableHeaderCell>{t('transparency:tokenManagement.table.expiresAt')}</TableHeaderCell>
                <TableHeaderCell>{t('transparency:tokenManagement.table.lastAccess')}</TableHeaderCell>
                <TableHeaderCell $align="center">{t('transparency:tokenManagement.table.accesses')}</TableHeaderCell>
                <TableHeaderCell $align="center">{t('transparency:tokenManagement.table.status')}</TableHeaderCell>
                <TableHeaderCell $align="center">{t('transparency:tokenManagement.table.actions')}</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {tokenList.map((token) => {
                const status = getTokenStatus(token);
                const badgeProps = getStatusBadgeProps(status.key);
                return (
                  <TableRow key={token._id}>
                    <TableCell><CellText>{token.recipientEmail}</CellText></TableCell>
                    <TableCell><CellMuted>{token.recipientName || '-'}</CellMuted></TableCell>
                    <TableCell>
                      <CellDate>{formatDateTimeWithTimezone(token.createdAt, selectedTenant?.timezone, companyLocale)}</CellDate>
                    </TableCell>
                    <TableCell>
                      <CellDate>{formatDateTimeWithTimezone(token.expiresAt, selectedTenant?.timezone, companyLocale)}</CellDate>
                    </TableCell>
                    <TableCell>
                      <CellDate>{token.lastUsedAt ? formatDateTimeWithTimezone(token.lastUsedAt, selectedTenant?.timezone, companyLocale) : t('transparency:tokenManagement.never')}</CellDate>
                    </TableCell>
                    <TableCell $align="center">
                      <AccessCount>{token.accessCount}</AccessCount>
                    </TableCell>
                    <TableCell $align="center">
                      <StatusBadge {...badgeProps}>{status.label}</StatusBadge>
                    </TableCell>
                    <TableCell $align="center">
                      <IconBtn
                        onClick={() => handleRevoke(token._id, token.recipientEmail)}
                        disabled={!canRevoke(token) || revokeMutation.isLoading}
                        title={t('transparency:tokenManagement.actions.revoke')}
                        aria-label={t('transparency:tokenManagement.actions.revoke')}
                      >
                        <RevokeSVG />
                      </IconBtn>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableBorder>
      )}

      {/* Create Token Dialog */}
      <Dialog.custom
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        title={t('transparency:tokenManagement.dialog.createTitle')}
        actions={
          <ModalActions>
            <ModalBtn $variant="secondary" onClick={() => setCreateDialogOpen(false)}>
              {t('common:buttons.cancel')}
            </ModalBtn>
            <ModalBtn type="submit" form="create-token-form" disabled={createMutation.isLoading}>
              {createMutation.isLoading ? <DotsLoader /> : t('transparency:tokenManagement.actions.create')}
            </ModalBtn>
          </ModalActions>
        }
      >
        <form id="create-token-form" onSubmit={handleSubmit(onSubmit)}>
          <FormField>
            <Label htmlFor="recipientEmail">{t('transparency:tokenManagement.fields.recipientEmail')} *</Label>
            <Controller
              control={control}
              name="recipientEmail"
              rules={{
                required: t('transparency:tokenManagement.validation.emailRequired'),
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('transparency:tokenManagement.validation.emailInvalid') },
              }}
              render={({ field }) => (
                <TextField {...field} id="recipientEmail" type="email" fullWidth error={!!errors.recipientEmail} helperText={errors.recipientEmail?.message} placeholder="parceiro@empresa.com" required />
              )}
            />
          </FormField>

          <FormField>
            <Label htmlFor="recipientName">{t('transparency:tokenManagement.fields.recipientName')}</Label>
            <Controller
              control={control}
              name="recipientName"
              render={({ field }) => (
                <TextField {...field} id="recipientName" type="text" fullWidth />
              )}
            />
          </FormField>

          <FormField>
            <Label htmlFor="expirationHours">{t('transparency:tokenManagement.fields.expiry')}</Label>
            <Controller
              control={control}
              name="expirationHours"
              render={({ field: { onChange, value } }) => (
                <Select
                  name="expirationHours"
                  value={value}
                  onChange={onChange}
                  options={[24, 48, 72, 168].map((h) => ({ value: h, label: t(`transparency:tokenManagement.expiry.${h}`) }))}
                />
              )}
            />
          </FormField>

          <InfoBox>
            <InfoBoxText>{t('transparency:tokenManagement.dialog.createInfo')}</InfoBoxText>
          </InfoBox>
        </form>
      </Dialog.custom>

      {/* Token Result Dialog */}
      <Dialog.custom
        open={showTokenResult}
        onClose={handleCloseTokenResult}
        title={t('transparency:tokenManagement.notifications.createSuccess')}
        actions={
          <ModalActions>
            <ModalBtn onClick={handleCopyToken}>
              <CopySVG />
              {t('transparency:tokenManagement.actions.copy')}
            </ModalBtn>
            <ModalBtn $variant="secondary" onClick={handleCloseTokenResult}>
              {t('transparency:tokenManagement.actions.close')}
            </ModalBtn>
          </ModalActions>
        }
      >
        <TokenHint>{t('transparency:tokenManagement.dialog.tokenCopyHint')}</TokenHint>
        <TokenWarning>{t('transparency:tokenManagement.dialog.tokenWarning')}</TokenWarning>
        <TextField
          id="token-result-string"
          multiline
          rows={4}
          fullWidth
          inputProps={{ readOnly: true }}
          sx={TOKEN_TEXTAREA_SX}
          value={createdToken}
        />
      </Dialog.custom>
    </Card>
  );
};
